import { ApiService } from "./api.service";
import type {
  TAssignmentCandidatesPage,
  TAssignmentCandidatesQuery,
} from "@/types/assignment-candidates";

export class IssueMetadataService extends ApiService {
  async listAssignmentCandidates(
    slug: string,
    projectId: string,
    workItemId: string,
    { q, offset, limit }: TAssignmentCandidatesQuery = {},
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch({ q, offset, limit });
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/work-items/${workItemId}/assignment-candidates/${search}`,
    );
  }

  // Draft variant for the create dialog: no work item exists yet, so the
  // ranking window travels as explicit params.
  async listDraftAssignmentCandidates(
    slug: string,
    projectId: string,
    {
      q,
      offset,
      limit,
      startDate,
      endDate,
      estimateHours,
      capLoadToWorkingHours,
    }: TAssignmentCandidatesQuery & {
      startDate?: string;
      endDate?: string;
      estimateHours?: string;
      // Events: cap each candidate's load to their working day so an all-day
      // slot doesn't read as 24h of work (see ProjectAssignmentCandidatesEndpoint).
      capLoadToWorkingHours?: boolean;
    } = {},
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch(
      { q, offset, limit },
      {
        start_date: startDate,
        end_date: endDate,
        estimate_hours: estimateHours,
        cap_load_to_working_hours: capLoadToWorkingHours ? "true" : undefined,
      },
    );
    return this.get(
      `/api/workspaces/${slug}/projects/${projectId}/assignment-candidates/${search}`,
    );
  }

  async listWorkspaceDraftAssignmentCandidates(
    slug: string,
    {
      q,
      offset,
      limit,
      startDate,
      endDate,
      estimateHours,
      capLoadToWorkingHours,
    }: TAssignmentCandidatesQuery & {
      startDate?: string;
      endDate?: string;
      estimateHours?: string;
      capLoadToWorkingHours?: boolean;
    } = {},
  ): Promise<TAssignmentCandidatesPage> {
    const search = buildCandidatesSearch(
      { q, offset, limit },
      {
        start_date: startDate,
        end_date: endDate,
        estimate_hours: estimateHours,
        cap_load_to_working_hours: capLoadToWorkingHours ? "true" : undefined,
      },
    );
    return this.get(`/api/workspaces/${slug}/assignment-candidates/${search}`);
  }
}

function buildCandidatesSearch(
  { q, offset, limit }: TAssignmentCandidatesQuery,
  extra: Record<string, string | undefined> = {},
): string {
  const params = new URLSearchParams();
  const query = q?.trim();
  if (query) params.set("q", query);
  if (offset) params.set("offset", String(offset));
  if (limit) params.set("limit", String(limit));
  for (const [key, value] of Object.entries(extra)) {
    if (value?.trim()) params.set(key, value.trim());
  }
  return params.size > 0 ? `?${params.toString()}` : "";
}
